import { useCallback, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { MetricData } from '../models/metricData';
import { Metric } from '../models/metric';
import { getMetrics, getMetricsList, updateMetricFavorite } from '../services/metrics';
import { useDataContext } from '../components/data';

export function useMetricsLogic() {
  const { t } = useTranslation();
  const { selectedCompany, updateMetricFilter } = useDataContext();
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [metricsValues, setMetricsValues] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFavorite, setLoadingFavorite] = useState<string | null>(null);

  const setMetricsData = useCallback(() => {
    if (metricsValues.length > 0) {
      const data: any = metricsValues
        .map(
          (m) =>
            new MetricData({
              metrics_name: m.metrics_name,
              metrics_values: m.metrics_values,
              options: [],
              data: [],
              description:
                metrics.find(
                  (mm) =>
                    mm.metricName?.toLowerCase() === m.metrics_name?.toLowerCase() ||
                    mm.name?.toLowerCase() === m.metrics_name?.toLowerCase()
                )?.description || '',
              favorite:
                metrics.find(
                  (mm) =>
                    mm.metricName?.toLowerCase() === m.metrics_name?.toLowerCase() ||
                    mm.name?.toLowerCase() === m.metrics_name?.toLowerCase()
                )?.favorite || false,
              isDash:
                metrics.find(
                  (mm) =>
                    mm.metricName?.toLowerCase() === m.metrics_name?.toLowerCase() ||
                    mm.name?.toLowerCase() === m.metrics_name?.toLowerCase()
                )?.isDash || false,
              id:
                metrics.find(
                  (mm) =>
                    mm.metricName?.toLowerCase() === m.metrics_name?.toLowerCase() ||
                    mm.name?.toLowerCase() === m.metrics_name?.toLowerCase()
                )?.id || '',
            })
        )
        .sort(
          (a, b) =>
            // If favorite is true, it should come first
            (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0) ||
            // Sort by name if both are favorite or not
            a.metrics_name.localeCompare(b.metrics_name)
        );
      updateMetricFilter(data);
    }
  }, [metrics, metricsValues, updateMetricFilter]);

  // Llama a getMetrics y callMetricsValues, luego actualiza el filtro
  const fetchAndUpdateMetrics = useCallback(async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      Promise.all([
        getMetrics(selectedCompany, undefined, undefined, undefined, {
          isDash: 'true',
        }),
        getMetricsList(selectedCompany),
      ]).then(([metricsRes, valuesRes]) => {
        setMetrics(metricsRes);
        setMetricsValues(valuesRes);
      });

      // Ordena favoritos primero
      // merged.sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
      // updateMetricFilter(merged);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany]);

  const handleToggleFavorite = useCallback(
    async (row: MetricData) => {
      if (!row.id) return;
      setLoadingFavorite(row.id);
      const updated = await updateMetricFavorite(row.id, !row.favorite);
      if (!updated) {
        enqueueSnackbar(t('error.updating'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('success.updated'), { variant: 'success' });
        row.setFavorite(!row.favorite);
      }
      setLoadingFavorite(null);
    },
    [t]
  );

  useEffect(() => {
    if (!selectedCompany) return undefined;
    fetchAndUpdateMetrics();
    const interval = setInterval(fetchAndUpdateMetrics, 300000); // 5 minutos
    return () => clearInterval(interval);
  }, [fetchAndUpdateMetrics, selectedCompany]);

  useEffect(() => {
    if (metricsValues.length > 0) {
      setMetricsData();
    }
  }, [metricsValues, setMetricsData]);

  return {
    metrics,
    metricsValues,
    loading,
    fetchAndUpdateMetrics,
    loadingFavorite,
    handleToggleFavorite,
  };
}
